import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Modal base reutilizable con accesibilidad de serie (lo que les falta a los
 * modales de drill-down actuales — ver ANALISIS-CRITICO H14):
 *  - `role="dialog"` + `aria-modal` + `aria-label`
 *  - cierre con ESC y con click en el overlay
 *  - scroll lock del body y foco inicial en el diálogo
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Gradiente Tailwind del header (`from-… via-… to-…`). */
  headerGradient?: string;
  icon?: ReactNode;
  /** Acciones a la derecha del header (ej. botón Exportar). */
  headerRight?: ReactNode;
  /** Ancho máximo Tailwind (default `max-w-6xl`). */
  maxWidth?: string;
  children: ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  headerGradient = 'from-slate-700 via-slate-800 to-slate-900',
  icon,
  headerRight,
  maxWidth = 'max-w-6xl',
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={`flex max-h-[90vh] w-full ${maxWidth} flex-col overflow-hidden rounded-2xl bg-white shadow-2xl outline-none`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
      >
        <header
          className={`flex shrink-0 items-center justify-between gap-3 bg-gradient-to-r ${headerGradient} px-5 py-3 text-white`}
        >
          <div className="flex min-w-0 items-center gap-2">
            {icon}
            <h2 className="truncate text-base font-bold">{title}</h2>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {headerRight}
            <button
              onClick={onClose}
              aria-label="Cerrar"
              className="rounded-lg p-1.5 text-white/80 hover:bg-white/15"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
